/**
 * Nav (Seed-style) drawer enhancements
 *
 * Layered on top of the theme's <navigation-drawer mega-menu> custom element
 * (defined in sections.js.liquid) without modifying it.
 *
 *  1. POSITION — anchor the desktop dropdown directly below the sticky
 *     header (--nav-seed-drawer-top) AND left-align its panel under the
 *     trigger that opened it (--nav-seed-drawer-left), so the dropdown lines
 *     up with the menu item instead of the viewport edge. Both are set at
 *     the moment of open and re-synced on scroll/resize while open.
 *
 *  2. HOVER-OPEN (client request) — Seed opens on hover, not click. We open
 *     on trigger pointerenter after a short intent delay, and close on
 *     mouse-leave (with a grace period that guards the trigger→panel
 *     travel gap). A click immediately after a hover-open is swallowed so
 *     the theme's click-toggle doesn't close what the hover just opened.
 *     Hover-open only runs for fine pointers (mouse) on desktop; touch and
 *     mobile fall back to the theme's native click toggle.
 *
 *  3. DISMISS — mouse-leave (above) or native click-outside via the drawer
 *     overlay. We can't use drawer.matches(':hover'): the drawer's
 *     transparent click-outside overlay is a full-viewport shadow child of
 *     the host, so the host counts as :hover everywhere while open. The
 *     visible panel + trigger are hit-tested geometrically instead.
 *
 * Desktop only — the mobile sidebar drawer keeps its own controls.
 */

(() => {
  const DESKTOP = '(min-width: 1000px)';
  const FINE_POINTER = '(hover: hover) and (pointer: fine)';
  const LEAVE_GRACE_MS = 300;
  const HOVER_INTENT_MS = 45;
  const CLICK_SWALLOW_MS = 500;
  // Horizontal offset from the drawer host's left edge to the visible panel
  // (::part(body)) left edge at >=1000px: 16px host padding + 12px body
  // margin = 28px. Host is positioned this much left of the trigger so the
  // panel's left edge lands under the trigger's left edge.
  const BODY_OFFSET_X = 28;

  const isDesktop = () => window.matchMedia(DESKTOP).matches;
  const canHover = () => window.matchMedia(FINE_POINTER).matches;

  const headerEl = () =>
    document.querySelector('.shopify-section--header') ||
    document.querySelector('store-header')?.closest('.shopify-section') ||
    document.querySelector('store-header');

  // ── 1. Position: top under the nav row, left under the trigger ──────────
  const root = document.documentElement;

  // Anchor to the NAV ROW bottom (the trigger's bottom edge), not the whole
  // header-section bottom — the section has extra padding below the row that
  // pushed the panel too far down. The triggers share the row, so any one
  // gives the row bottom; falls back to the header section if none found.
  const navRowBottom = () => {
    const t = document.querySelector('button[aria-controls^="navigation-drawer"]');
    if (t) return Math.max(0, Math.round(t.getBoundingClientRect().bottom));
    const header = headerEl();
    return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
  };

  const syncTop = () => {
    root.style.setProperty('--nav-seed-drawer-top', navRowBottom() + 'px');
  };

  const alignLeftTo = (trigger) => {
    if (!trigger) return;
    // Panel's visible left edge (body) should sit under the trigger's left
    // edge; the host sits BODY_MARGIN_X further left to compensate for the
    // body's margin. Clamp so a right-most trigger can't push the panel off
    // the right edge of the viewport.
    const r = trigger.getBoundingClientRect();
    const width = 420 + BODY_OFFSET_X + 12;
    const maxLeft = window.innerWidth - width - 8;
    const left = Math.max(8, Math.min(Math.round(r.left) - BODY_OFFSET_X, maxLeft));
    root.style.setProperty('--nav-seed-drawer-left', left + 'px');
  };

  const positionFor = (trigger) => {
    syncTop();
    alignLeftTo(trigger);
  };

  const anyDrawerOpen = () =>
    !!document.querySelector('navigation-drawer[mega-menu][open]');

  let rafPending = false;
  const onScroll = () => {
    if (!anyDrawerOpen() || rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      syncTop();
    });
  };

  // Shared "last hover-open time" so the trigger-switch / click-swallow logic
  // can tell a hover-open from a deliberate click.
  let lastHoverOpenAt = 0;

  // ── 2 + 3. Hover-open + mouse-leave dismiss, per drawer ─────────────────
  const wireDrawer = (drawer) => {
    if (drawer.__navSeedWired) return;
    drawer.__navSeedWired = true;

    const trigger = document.querySelector(`[aria-controls="${drawer.id}"]`);
    let leaveTimer = null;
    let hoverTimer = null;
    let lastX = -1;
    let lastY = -1;

    const within = (el, pad) => {
      if (!el || lastX < 0) return false;
      const r = el.getBoundingClientRect();
      return (
        lastX >= r.left - pad &&
        lastX <= r.right + pad &&
        lastY >= r.top - pad &&
        lastY <= r.bottom + pad
      );
    };
    const pointerOverUi = () => {
      const body = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part~="body"]');
      return within(body || drawer, 8) || within(trigger, 8);
    };

    const clearLeave = () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };
    const clearHover = () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    };

    const scheduleClose = () => {
      clearLeave();
      leaveTimer = setTimeout(() => {
        leaveTimer = null;
        if (pointerOverUi()) return;
        if (typeof drawer.hide === 'function') drawer.hide();
      }, LEAVE_GRACE_MS);
    };

    const stateObserver = new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) clearLeave();
    });
    stateObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });

    const onPointerTravel = (event) => {
      lastX = event.clientX;
      lastY = event.clientY;
      if (!isDesktop()) return;
      if (!drawer.hasAttribute('open')) return;
      if (pointerOverUi()) clearLeave();
      else if (!leaveTimer) scheduleClose();
    };
    document.addEventListener('pointermove', onPointerTravel, { passive: true });
    document.addEventListener('mousemove', onPointerTravel, { passive: true });

    if (trigger) {
      // Hover-open (desktop, fine pointer only).
      trigger.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        if (!isDesktop() || !canHover()) return;
        clearLeave();
        clearHover();
        hoverTimer = setTimeout(() => {
          hoverTimer = null;
          if (!drawer.hasAttribute('open')) {
            // Close any sibling drawer first, then position + open this one.
            const other = document.querySelector('navigation-drawer[mega-menu][open]');
            if (other && other !== drawer && typeof other.hide === 'function') other.hide();
            positionFor(trigger);
            lastHoverOpenAt = performance.now();
            if (typeof drawer.show === 'function') drawer.show();
          }
        }, HOVER_INTENT_MS);
      });

      trigger.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        clearHover();
        if (!isDesktop()) return;
        scheduleClose();
      });

      // Keep top/left correct for click-opens (touch, or click before the
      // hover delay elapses) too.
      trigger.addEventListener('pointerdown', () => positionFor(trigger), true);
    }
  };

  // Click handling on triggers:
  //  - swallow a click that lands right after a hover-open (so it doesn't
  //    toggle the freshly-opened drawer shut);
  //  - while a drawer is open, a click on the OTHER trigger swaps panels in
  //    one click (the open drawer's full-viewport overlay would otherwise
  //    just dismiss and eat the click).
  const wireTriggerClicks = () => {
    document.addEventListener(
      'click',
      (event) => {
        if (!isDesktop()) return;
        const triggers = [...document.querySelectorAll('button[aria-controls^="navigation-drawer"]')];
        const hit = triggers.find((t) => {
          const r = t.getBoundingClientRect();
          return (
            event.clientX >= r.left &&
            event.clientX <= r.right &&
            event.clientY >= r.top &&
            event.clientY <= r.bottom
          );
        });
        if (!hit) return;

        const target = document.getElementById(hit.getAttribute('aria-controls'));
        const open = document.querySelector('navigation-drawer[mega-menu][open]');

        // Just hover-opened THIS drawer → swallow the toggle-close.
        if (
          open &&
          open === target &&
          performance.now() - lastHoverOpenAt < CLICK_SWALLOW_MS
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        // A different drawer is open → switch to the clicked one.
        if (open && target && open !== target) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (typeof open.hide === 'function') open.hide();
          positionFor(hit);
          setTimeout(() => {
            if (typeof target.show === 'function') target.show();
          }, 120);
        }
      },
      true
    );
  };

  // ── Seed-style mobile tabbed nav: switch category panels ────────────────
  const wireMobileTabs = () => {
    document.querySelectorAll('[data-nav-seed-mobile]').forEach((root) => {
      if (root.__navSeedTabsWired) return;
      root.__navSeedTabsWired = true;
      const tabs = [...root.querySelectorAll('[data-nsm-tab]')];
      const panels = [...root.querySelectorAll('[data-nsm-panel]')];
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = tab.getAttribute('data-nsm-tab');
          tabs.forEach((t) => {
            const on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          panels.forEach((p) => {
            p.classList.toggle('is-active', p.getAttribute('data-nsm-panel') === idx);
          });
        });
      });
    });
  };

  const init = () => {
    syncTop();
    document.querySelectorAll('navigation-drawer[mega-menu]').forEach(wireDrawer);
    wireTriggerClicks();
    wireMobileTabs();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', syncTop, { passive: true });
    window.addEventListener('load', syncTop);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
